import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Image, Loader, Thumbnail } from '@wix/design-system';
import imageClientAPI from 'image-client-api/dist/imageClientSDK';
import React, { useContext } from 'react';

import { RicosContext } from './../../../../../ricos-context';
import { ErrorMessageMap } from './../../../../../wix-rich-content-common/lib/ErrorMessageMap';

import { MEDIA_SETTINGS_SORTABLE_ITEMS } from '../../../../data-hooks';
import { GALLERY_ITEMS_TYPES } from '../../../../defaults';
import type { GalleryItem } from '../../common-types';
import classes from './sortable-item-scss';

const isLocalObjectUrl = (
  item: GalleryItem,
  type: (typeof GALLERY_ITEMS_TYPES)[keyof typeof GALLERY_ITEMS_TYPES]
) => {
  return !!item[type]?.media?.src?.url && item[type]?.media?.src?.url?.indexOf('data:') !== -1;
};

type SortableItemProps = {
  item: GalleryItem;
  onClick: () => void;
  onDoubleClick: () => void;
  className: string;
};

export const SortableItem: React.FC<SortableItemProps> = ({
  item,
  onClick,
  onDoubleClick,
  className,
}) => {
  const { t, environment } = useContext(RicosContext);
  const { attributes, listeners, setNodeRef, transform } = useSortable({
    id: item.id,
  });

  if (item.error) {
    return (
      <div className={className}>
        <Thumbnail
          size="small"
          selected={item.selected}
          onClick={onClick}
          dataHook={MEDIA_SETTINGS_SORTABLE_ITEMS}
          className={classes.itemImage}
          backgroundImage={
            <Loader
              status="error"
              statusMessage={t(
                item.error.key
                  ? ErrorMessageMap[item.error.key as unknown as keyof typeof ErrorMessageMap]
                  : 'UploadFile_Error_Generic_Toast_Multiple'
              )}
            />
          }
        />
      </div>
    );
  }

  const itemType = item?.image ? GALLERY_ITEMS_TYPES.IMAGE : GALLERY_ITEMS_TYPES.VIDEO;

  const prefix = (() => {
    if (Number(item[itemType]?.media?.src?.url?.indexOf('/')) < 0) {
      return 'media/';
    }
    return '';
  })();

  const src =
    itemType === GALLERY_ITEMS_TYPES.VIDEO
      ? item.video?.thumbnail?.src?.url
      : item.image?.media?.src?.url ?? item.image?.media?.src?.id;

  if (isLocalObjectUrl(item, itemType) || !item[itemType]) {
    return null;
  }

  const url = imageClientAPI.getScaleToFillImageURL(
    prefix + src,
    item[itemType]?.media?.width,
    item[itemType]?.media?.height,
    100,
    100
  );

  return (
    <div
      ref={setNodeRef}
      className={className}
      style={{
        transform: CSS.Transform.toString(transform),
      }}
      {...attributes}
      {...listeners}
    >
      <Thumbnail
        size="small"
        selected={item.selected}
        onClick={e => {
          if (e.detail === 2) {
            onDoubleClick();
            return;
          }
          onClick();
        }}
        dataHook={MEDIA_SETTINGS_SORTABLE_ITEMS}
        className={classes.itemImage}
        backgroundImage={
          src ? ( // src is an empty string during file upload
            <Image
              draggable="false"
              id={item.id}
              role="img"
              aria-label="Gallery Item Thumbnail"
              src={url}
              fit="cover"
              borderRadius={environment === 'liveSite' ? 0 : undefined}
              alt={t('GalleryPlugin_Item_Thumbnail_Alt')}
            />
          ) : (
            <Loader />
          )
        }
      />
    </div>
  );
};
